import { useState, useCallback } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermit, useUpdatePermitStatus } from "@/hooks/use-permits";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useToast } from "@/hooks/use-toast";
import { permitTypeVariant, permitStatusVariant } from "@/components/permits/permit-columns";
import { PermitTabs, type PermitTab } from "@/components/permits/permit-tabs";
import { OverviewTab } from "@/components/permits/overview-tab";
import { ParametersTab } from "@/components/permits/parameters-tab";
import { RenewDialog } from "@/components/permits/renew-dialog";
import { ModificationDialog } from "@/components/permits/modification-dialog";
import { TransferDialog } from "@/components/permits/transfer-dialog";
import { RevokeDialog } from "@/components/permits/revoke-dialog";
import { HistoryTab } from "@/components/permits/history-tab";
import { PermitSamplingTab } from "@/components/permits/permit-sampling-tab";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  PageHeader,
  Breadcrumbs,
  PageSkeleton,
  Badge,
  Button,
  StatusDropdown,
} from "@/components/ui";
import { EntityDocuments } from "@/components/documents/entity-documents";

export const Route = createFileRoute("/_authenticated/permits/$permitId/")({
  component: PermitDetailPage,
});

function PermitDetailPage() {
  const { t } = useTranslation("permits");
  const { permitId } = Route.useParams();
  const toast = useToast();
  const orgId = useAppStore((s) => s.activeOrgId);

  const [activeTab, setActiveTab] = useState<PermitTab>("overview");
  const [activeDialog, setActiveDialog] = useState<
    "renew" | "modification" | "transfer" | "revocation" | null
  >(null);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const { data: permit, isLoading, isError, error } = usePermit(orgId, permitId);
  useRecentPageTitle(permit?.permit_number ?? undefined);
  const statusMutation = useUpdatePermitStatus(orgId, permitId);

  const handlePermitAction = useCallback((action: string) => {
    if (
      action === "renew" ||
      action === "modification" ||
      action === "transfer" ||
      action === "revocation"
    ) {
      setActiveDialog(action);
    }
  }, []);

  const closeDialog = useCallback(() => setActiveDialog(null), []);

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !permit) {
    return (
      <div className="p-8">
        <Breadcrumbs items={[{ label: t("title"), to: "/permits" }]} />
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("unknownError", { ns: "common" })}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[{ label: t("title"), to: "/permits" }, { label: permit.permit_number }]}
      />
      <PageHeader
        title={permit.permit_number}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={permitTypeVariant(permit.permit_type)}>
              {t(`types.${permit.permit_type}`)}
            </Badge>
            <Badge variant={permitStatusVariant(permit.status)}>
              {t(`statuses.${permit.status}`)}
            </Badge>
            <Link to="/permits/$permitId/edit" params={{ permitId }}>
              <Button variant="secondary">{t("editPermit")}</Button>
            </Link>
            <StatusDropdown
              currentStatus={permit.status}
              statuses={[
                "draft",
                "active",
                "expired",
                "terminated",
                "revoked",
                "administratively_continued",
              ]}
              isPending={statusMutation.isPending}
              onChange={(status) => setPendingStatus(status)}
              labelKey="permits:detail.changeStatus"
              statusKeyPrefix="permits:statuses"
            />
          </div>
        }
      />

      <div className="mt-6">
        <PermitTabs activeTab={activeTab} onTabChange={setActiveTab} />
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          className="mt-6"
        >
          <TabContent
            tab={activeTab}
            permit={permit}
            permitId={permitId}
            orgId={orgId}
            onTabChange={setActiveTab}
            onPermitAction={handlePermitAction}
          />
        </div>
      </div>

      <RenewDialog
        open={activeDialog === "renew"}
        permitId={permitId}
        permitNumber={permit.permit_number}
        onClose={closeDialog}
      />
      <ModificationDialog
        open={activeDialog === "modification"}
        permitId={permitId}
        onClose={closeDialog}
      />
      <TransferDialog
        open={activeDialog === "transfer"}
        permitId={permitId}
        permitNumber={permit.permit_number}
        onClose={closeDialog}
      />
      <RevokeDialog
        open={activeDialog === "revocation"}
        permitId={permitId}
        permitNumber={permit.permit_number}
        onClose={closeDialog}
      />
      <ConfirmDialog
        open={!!pendingStatus}
        title={t("statusChange.confirmTitle")}
        message={t("statusChange.confirmMessage", { status: pendingStatus })}
        confirmLabel={t("statusChange.confirmTitle")}
        onConfirm={() => {
          if (pendingStatus) {
            statusMutation.mutate(
              // StatusDropdown returns a generic string; cast to the generated union type
              { status: pendingStatus as components["schemas"]["PermitStatus"] },
              {
                onSuccess: () => toast.success(t("toast.statusUpdateSuccess")),
                onError: () => toast.error(t("toast.statusUpdateError")),
              },
            );
          }
          setPendingStatus(null);
        }}
        onCancel={() => setPendingStatus(null)}
        variant="primary"
      />
    </div>
  );
}

function TabContent({
  tab,
  permit,
  permitId,
  orgId,
  onTabChange,
  onPermitAction,
}: {
  tab: PermitTab;
  permit: NonNullable<ReturnType<typeof usePermit>["data"]>;
  permitId: string;
  orgId: string | null;
  onTabChange: (tab: PermitTab) => void;
  onPermitAction: (action: string) => void;
}) {
  switch (tab) {
    case "overview":
      return (
        <OverviewTab permit={permit} onTabChange={onTabChange} onPermitAction={onPermitAction} />
      );
    case "parameters":
      return (
        <ParametersTab
          permitId={permitId}
          parameterCount={permit.parameter_count}
          limitCount={permit.limit_count}
          outfallCount={permit.outfall_count}
        />
      );
    case "sampling":
      return <PermitSamplingTab permitId={permitId} />;
    case "history":
      return <HistoryTab permit={permit} />;
    case "documents":
      return orgId ? (
        <EntityDocuments entityType="permit" entityId={permit.id} orgId={orgId} />
      ) : null;
  }
}
